/**
 * §7.2 screen 4 — Directory: mahallas CRUD + yettiligi CRUD + photo upload.
 *
 * Two different capabilities live on one screen, and they are NOT nested:
 * `mahalla.write` is role `admin`, `yettiligi.write` is `content_editor` or
 * `admin`. A content editor therefore edits the staff directory but cannot touch
 * the mahalla registry — and, per D-032 §4.0, still sees nothing of any citizen.
 *
 * Neither entity is ever hard-deleted. A mahalla is referenced by murojaats
 * (`ON DELETE RESTRICT`) and a departing official's row is kept for the audit
 * window (§2.3), so "delete" is `is_active=false` throughout.
 */
import { useCallback, useEffect, useState } from 'react';
import type { components } from '@navoiy/api-types';
import { YETTILIGI_ROLES, STAFF_PHOTO_MAX_BYTES, type YettiligiRole } from '@navoiy/domain';
import { ApiError, api, apiBaseUrl, errorKey, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { useMahallas } from '../lib/useMahallas.js';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorBanner,
  Field,
  Modal,
  Select,
  Spinner,
  Table,
  Td,
  Th,
  TextInput,
  Tr,
  WarningBanner,
} from '../ui/index.jsx';
import { HoursEditor, EMPTY_HOURS } from '../components/HoursEditor.jsx';

type MahallaAdmin = components['schemas']['MahallaAdmin'];
type MemberAdmin = components['schemas']['YettiligiMemberAdmin'];
type WeeklyHours = components['schemas']['WeeklyHours'];

type Tab = 'mahallas' | 'yettiligi';

export function DirectoryScreen(): React.JSX.Element {
  const { t, locale } = useI18n();
  const { allows } = useSession();
  const [tab, setTab] = useState<Tab>(allows('mahalla.write') ? 'mahallas' : 'yettiligi');

  const canMahalla = allows('mahalla.write');
  const canYettiligi = allows('yettiligi.write');
  const { mahallas } = useMahallas(locale);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-2">
        {canMahalla ? (
          <Button tone={tab === 'mahallas' ? 'primary' : 'secondary'} onClick={() => setTab('mahallas')}>
            {t('admin.directory.mahallas')}
          </Button>
        ) : null}
        <Button tone={tab === 'yettiligi' ? 'primary' : 'secondary'} onClick={() => setTab('yettiligi')}>
          {t('admin.directory.yettiligi')}
        </Button>
      </div>

      {tab === 'mahallas' && canMahalla ? <MahallaPanel /> : null}
      {tab === 'yettiligi' ? (
        <YettiligiPanel
          canWrite={canYettiligi}
          mahallaOptions={mahallas.map((m) => ({ id: m.id, name: m.name }))}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mahallas
// ---------------------------------------------------------------------------

const BLANK_MAHALLA = {
  code: '',
  name_uz_latn: '',
  name_uz_cyrl: '',
  name_ru: '',
  name_en: '',
  landmark_uz_latn: '',
  landmark_uz_cyrl: '',
  landmark_ru: '',
  landmark_en: '',
  sort_order: 1000,
  is_active: true,
};

function MahallaPanel(): React.JSX.Element {
  const { t } = useI18n();
  const { expire } = useSession();
  const [rows, setRows] = useState<MahallaAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<MahallaAdmin | 'new' | null>(null);

  const handle = useCallback(
    (caught: unknown) => {
      if (caught instanceof ApiError && caught.status === 401) return expire();
      const { key, values } = errorKey(caught);
      setError(t(key, values));
    },
    [expire, t],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = unwrap(await api().GET('/admin/mahallas'));
      setRows(data.items);
      setError(null);
    } catch (caught) {
      handle(caught);
    } finally {
      setLoading(false);
    }
  }, [handle]);

  useEffect(() => {
    void load();
  }, [load]);

  async function deactivate(row: MahallaAdmin): Promise<void> {
    if (!window.confirm(t('admin.directory.deactivateMahallaConfirm'))) return;
    try {
      unwrap(
        await api().DELETE('/admin/mahallas/{mahalla_id}', {
          params: { path: { mahalla_id: row.id } },
        }),
      );
      await load();
    } catch (caught) {
      handle(caught);
    }
  }

  return (
    <Card
      title={t('admin.directory.mahallas')}
      actions={<Button onClick={() => setEditing('new')}>{t('admin.directory.addMahalla')}</Button>}
    >
      <ErrorBanner message={error} />
      {loading ? <Spinner label={t('admin.common.loading')} /> : null}
      {!loading && rows.length === 0 ? <EmptyState message={t('admin.directory.mahallaEmpty')} /> : null}
      {rows.length > 0 ? (
        <Table
          head={
            <tr>
              <Th>{t('admin.directory.code')}</Th>
              <Th>{t('admin.directory.nameUzLatn')}</Th>
              <Th>{t('admin.directory.sortOrder')}</Th>
              <Th>{t('admin.directory.isActive')}</Th>
              <Th>{t('admin.common.actions')}</Th>
            </tr>
          }
        >
          {rows.map((row) => (
            <Tr key={row.id}>
              <Td className="font-semibold">{row.code}</Td>
              <Td>{row.name_uz_latn}</Td>
              <Td>{row.sort_order}</Td>
              <Td>
                <Badge tone={row.is_active ? 'success' : 'neutral'}>
                  {row.is_active ? t('admin.common.yes') : t('admin.common.no')}
                </Badge>
              </Td>
              <Td>
                <span className="flex gap-2">
                  <Button tone="ghost" onClick={() => setEditing(row)}>
                    {t('admin.common.edit')}
                  </Button>
                  {row.is_active ? (
                    <Button tone="ghost" onClick={() => void deactivate(row)}>
                      {t('admin.directory.deactivate')}
                    </Button>
                  ) : null}
                </span>
              </Td>
            </Tr>
          ))}
        </Table>
      ) : null}

      {editing ? (
        <MahallaDialog
          row={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
          onError={handle}
        />
      ) : null}
    </Card>
  );
}

function MahallaDialog({
  row,
  onClose,
  onSaved,
  onError,
}: {
  row: MahallaAdmin | null;
  onClose: () => void;
  onSaved: () => void;
  onError: (error: unknown) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const [form, setForm] = useState({ ...BLANK_MAHALLA, ...(row ?? {}) });
  const [busy, setBusy] = useState(false);

  function set<K extends keyof typeof form>(key: K, value: (typeof form)[K]): void {
    setForm({ ...form, [key]: value });
  }

  async function save(): Promise<void> {
    setBusy(true);
    try {
      const body = {
        code: form.code,
        name_uz_latn: form.name_uz_latn,
        name_uz_cyrl: form.name_uz_cyrl,
        name_ru: form.name_ru,
        name_en: form.name_en,
        landmark_uz_latn: form.landmark_uz_latn || undefined,
        landmark_uz_cyrl: form.landmark_uz_cyrl || undefined,
        landmark_ru: form.landmark_ru || undefined,
        landmark_en: form.landmark_en || undefined,
        sort_order: Number(form.sort_order),
        is_active: form.is_active,
      };
      if (row) {
        unwrap(
          await api().PATCH('/admin/mahallas/{mahalla_id}', {
            params: { path: { mahalla_id: row.id } },
            body,
          }),
        );
      } else {
        unwrap(await api().POST('/admin/mahallas', { body }));
      }
      onSaved();
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={row ? t('admin.directory.editMahalla') : t('admin.directory.addMahalla')}
      onClose={onClose}
      wide
      footer={
        <>
          <Button tone="secondary" onClick={onClose}>
            {t('admin.common.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void save()}>
            {t('admin.common.save')}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={t('admin.directory.code')}>
          <TextInput
            required
            maxLength={32}
            value={form.code}
            onChange={(event) => set('code', event.target.value)}
          />
        </Field>
        <Field label={t('admin.directory.sortOrder')}>
          <TextInput
            type="number"
            value={String(form.sort_order)}
            onChange={(event) => set('sort_order', Number(event.target.value))}
          />
        </Field>
        <Field label={t('admin.directory.nameUzLatn')}>
          <TextInput
            required
            maxLength={200}
            value={form.name_uz_latn}
            onChange={(event) => set('name_uz_latn', event.target.value)}
          />
        </Field>
        <Field label={t('admin.directory.nameUzCyrl')}>
          <TextInput
            required
            maxLength={200}
            value={form.name_uz_cyrl}
            onChange={(event) => set('name_uz_cyrl', event.target.value)}
          />
        </Field>
        <Field label={t('admin.directory.nameRu')}>
          <TextInput
            required
            maxLength={200}
            value={form.name_ru}
            onChange={(event) => set('name_ru', event.target.value)}
          />
        </Field>
        <Field label={t('admin.directory.nameEn')}>
          <TextInput
            required
            maxLength={200}
            value={form.name_en}
            onChange={(event) => set('name_en', event.target.value)}
          />
        </Field>
        <Field label={`${t('admin.directory.landmark')} (uz-Latn)`}>
          <TextInput
            maxLength={300}
            value={form.landmark_uz_latn ?? ''}
            onChange={(event) => set('landmark_uz_latn', event.target.value)}
          />
        </Field>
        <Field label={`${t('admin.directory.landmark')} (ru)`}>
          <TextInput
            maxLength={300}
            value={form.landmark_ru ?? ''}
            onChange={(event) => set('landmark_ru', event.target.value)}
          />
        </Field>
      </div>
      <Checkbox
        label={t('admin.directory.isActive')}
        checked={form.is_active}
        onChange={(event) => set('is_active', event.target.checked)}
      />
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Yettiligi
// ---------------------------------------------------------------------------

function YettiligiPanel({
  canWrite,
  mahallaOptions,
}: {
  canWrite: boolean;
  mahallaOptions: { id: string; name: string }[];
}): React.JSX.Element {
  const { t } = useI18n();
  const { expire } = useSession();
  const [mahallaId, setMahallaId] = useState('');
  const [rows, setRows] = useState<MemberAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<MemberAdmin | 'new' | null>(null);
  const [photoFor, setPhotoFor] = useState<MemberAdmin | null>(null);

  const handle = useCallback(
    (caught: unknown) => {
      if (caught instanceof ApiError && caught.status === 401) return expire();
      const { key, values } = errorKey(caught);
      setError(t(key, values));
    },
    [expire, t],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = unwrap(
        await api().GET('/admin/yettiligi', {
          params: { query: mahallaId ? { mahalla_id: mahallaId } : {} },
        }),
      );
      setRows(data.items);
      setError(null);
    } catch (caught) {
      handle(caught);
    } finally {
      setLoading(false);
    }
  }, [mahallaId, handle]);

  useEffect(() => {
    void load();
  }, [load]);

  async function deactivate(row: MemberAdmin): Promise<void> {
    if (!window.confirm(t('admin.directory.deactivateMemberConfirm'))) return;
    try {
      unwrap(
        await api().DELETE('/admin/yettiligi/{member_id}', {
          params: { path: { member_id: row.id } },
        }),
      );
      await load();
    } catch (caught) {
      handle(caught);
    }
  }

  return (
    <Card
      title={t('admin.directory.yettiligi')}
      actions={
        canWrite ? <Button onClick={() => setEditing('new')}>{t('admin.directory.addMember')}</Button> : null
      }
    >
      <div className="mb-4 max-w-sm">
        <Field label={t('admin.directory.selectMahalla')}>
          <Select value={mahallaId} onChange={(event) => setMahallaId(event.target.value)}>
            <option value="">{t('admin.common.all')}</option>
            {mahallaOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <ErrorBanner message={error} />
      {loading ? <Spinner label={t('admin.common.loading')} /> : null}
      {!loading && rows.length === 0 ? <EmptyState message={t('admin.directory.yettiligiEmpty')} /> : null}

      {rows.length > 0 ? (
        <Table
          head={
            <tr>
              <Th>{t('admin.directory.photo')}</Th>
              <Th>{t('admin.directory.role')}</Th>
              <Th>{t('admin.directory.fullName')}</Th>
              <Th>{t('admin.directory.phone')}</Th>
              <Th>{t('admin.directory.isActive')}</Th>
              <Th>{t('admin.common.actions')}</Th>
            </tr>
          }
        >
          {rows.map((row) => (
            <Tr key={row.id}>
              <Td>
                {row.photo_url ? (
                  <img
                    src={`${apiBaseUrl().replace(/\/api\/v1$/, '')}${row.photo_url}`}
                    alt=""
                    className="h-10 w-10 rounded-chip object-cover"
                  />
                ) : (
                  <span className="inline-block h-10 w-10 rounded-chip bg-surface-100" />
                )}
              </Td>
              <Td>{t(`shared.yettiligi.role.${row.role}`)}</Td>
              <Td className="font-semibold">{row.full_name}</Td>
              <Td>{row.phone}</Td>
              <Td>
                <Badge tone={row.is_active ? 'success' : 'neutral'}>
                  {row.is_active ? t('admin.common.yes') : t('admin.common.no')}
                </Badge>
              </Td>
              <Td>
                {canWrite ? (
                  <span className="flex flex-wrap gap-2">
                    <Button tone="ghost" onClick={() => setEditing(row)}>
                      {t('admin.common.edit')}
                    </Button>
                    <Button tone="ghost" onClick={() => setPhotoFor(row)}>
                      {t('admin.directory.photoUpload')}
                    </Button>
                    {row.is_active ? (
                      <Button tone="ghost" onClick={() => void deactivate(row)}>
                        {t('admin.directory.deactivate')}
                      </Button>
                    ) : null}
                  </span>
                ) : null}
              </Td>
            </Tr>
          ))}
        </Table>
      ) : null}

      {editing ? (
        <MemberDialog
          row={editing === 'new' ? null : editing}
          mahallaOptions={mahallaOptions}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void load();
          }}
          onError={handle}
        />
      ) : null}

      {photoFor ? (
        <PhotoDialog
          member={photoFor}
          onClose={() => setPhotoFor(null)}
          onSaved={() => {
            setPhotoFor(null);
            void load();
          }}
          onError={handle}
        />
      ) : null}
    </Card>
  );
}

function MemberDialog({
  row,
  mahallaOptions,
  onClose,
  onSaved,
  onError,
}: {
  row: MemberAdmin | null;
  mahallaOptions: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
  onError: (error: unknown) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const [mahallaId, setMahallaId] = useState(row?.mahalla_id ?? mahallaOptions[0]?.id ?? '');
  const [role, setRole] = useState<YettiligiRole>(row?.role ?? YETTILIGI_ROLES[0]);
  const [fullName, setFullName] = useState(row?.full_name ?? '');
  const [fullNameCyrl, setFullNameCyrl] = useState(row?.full_name_cyrl ?? '');
  const [phone, setPhone] = useState(row?.phone ?? '');
  const [phoneAlt, setPhoneAlt] = useState(row?.phone_alt ?? '');
  const [note, setNote] = useState(row?.note_uz_latn ?? '');
  const [hours, setHours] = useState<WeeklyHours>(row?.hours ?? EMPTY_HOURS);
  const [isActive, setIsActive] = useState(row?.is_active ?? true);
  const [sortOrder, setSortOrder] = useState(row?.sort_order ?? 1000);
  const [busy, setBusy] = useState(false);

  async function save(): Promise<void> {
    setBusy(true);
    try {
      const common = {
        role,
        full_name: fullName,
        full_name_cyrl: fullNameCyrl || undefined,
        phone,
        phone_alt: phoneAlt || undefined,
        hours,
        note_uz_latn: note || undefined,
        is_active: isActive,
        sort_order: Number(sortOrder),
      };
      if (row) {
        unwrap(
          await api().PATCH('/admin/yettiligi/{member_id}', {
            params: { path: { member_id: row.id } },
            body: common,
          }),
        );
      } else {
        unwrap(await api().POST('/admin/yettiligi', { body: { ...common, mahalla_id: mahallaId } }));
      }
      onSaved();
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={row ? t('admin.directory.editMember') : t('admin.directory.addMember')}
      onClose={onClose}
      wide
      footer={
        <>
          <Button tone="secondary" onClick={onClose}>
            {t('admin.common.cancel')}
          </Button>
          <Button disabled={busy} onClick={() => void save()}>
            {t('admin.common.save')}
          </Button>
        </>
      }
    >
      <WarningBanner>{t('admin.directory.phoneOfficeOnly')}</WarningBanner>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {!row ? (
          <Field label={t('admin.directory.selectMahalla')}>
            <Select value={mahallaId} onChange={(event) => setMahallaId(event.target.value)}>
              {mahallaOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}

        <Field label={t('admin.directory.role')}>
          <Select value={role} onChange={(event) => setRole(event.target.value as YettiligiRole)}>
            {YETTILIGI_ROLES.map((value) => (
              <option key={value} value={value}>
                {t(`shared.yettiligi.role.${value}`)}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('admin.directory.fullName')}>
          <TextInput
            required
            maxLength={200}
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
          />
        </Field>
        <Field label={t('admin.directory.fullNameCyrl')}>
          <TextInput
            maxLength={200}
            value={fullNameCyrl}
            onChange={(event) => setFullNameCyrl(event.target.value)}
          />
        </Field>
        <Field label={t('admin.directory.phone')}>
          <TextInput
            required
            maxLength={32}
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
          />
        </Field>
        <Field label={t('admin.directory.phoneAlt')}>
          <TextInput
            maxLength={32}
            value={phoneAlt}
            onChange={(event) => setPhoneAlt(event.target.value)}
          />
        </Field>
        <Field label={t('admin.directory.note')}>
          <TextInput
            maxLength={300}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
        <Field label={t('admin.directory.sortOrder')}>
          <TextInput
            type="number"
            value={String(sortOrder)}
            onChange={(event) => setSortOrder(Number(event.target.value))}
          />
        </Field>
      </div>

      <HoursEditor value={hours} onChange={setHours} />

      <Checkbox
        label={t('admin.directory.isActive')}
        checked={isActive}
        onChange={(event) => setIsActive(event.target.checked)}
      />
    </Modal>
  );
}

function PhotoDialog({
  member,
  onClose,
  onSaved,
  onError,
}: {
  member: MemberAdmin;
  onClose: () => void;
  onSaved: () => void;
  onError: (error: unknown) => void;
}): React.JSX.Element {
  const { t } = useI18n();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  async function upload(): Promise<void> {
    if (!file) return;
    // Client-side pre-check so a staff member on a slow link is not made to
    // upload 40 MB before the server refuses it. The server enforces it too.
    if (file.size > STAFF_PHOTO_MAX_BYTES) {
      setLocalError(t('admin.error.tooLarge'));
      return;
    }
    setBusy(true);
    setLocalError(null);
    try {
      const form = new FormData();
      form.append('photo', file);
      // openapi-fetch cannot express a multipart body from the generated types,
      // so this one call goes through fetch — same origin, same cookie.
      const response = await fetch(
        `${apiBaseUrl()}/admin/yettiligi/${encodeURIComponent(member.id)}/photo`,
        { method: 'POST', body: form, credentials: 'include' },
      );
      if (!response.ok) {
        const problem = await response.json().catch(() => null);
        throw new ApiError(response.status, problem, null);
      }
      onSaved();
    } catch (caught) {
      onError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={t('admin.directory.photoUpload')}
      onClose={onClose}
      footer={
        <>
          <Button tone="secondary" onClick={onClose}>
            {t('admin.common.cancel')}
          </Button>
          <Button disabled={busy || !file} onClick={() => void upload()}>
            {t('admin.common.save')}
          </Button>
        </>
      }
    >
      <ErrorBanner message={localError} />
      <Field label={t('admin.directory.photo')} hint={t('admin.directory.photoHint')}>
        <TextInput
          type="file"
          accept="image/jpeg,image/png"
          onChange={(event) => setFile(event.target.files?.[0] ?? null)}
        />
      </Field>
    </Modal>
  );
}
